import { useEffect, useLayoutEffect, useMemo, useRef, useState, type FormEvent, type KeyboardEvent, type MouseEvent, type PointerEvent, type UIEvent } from 'react'
import { Link, useLocation } from 'react-router-dom'
import {
  SELECTABLE_OUTCOMES,
  addDays,
  applyOwnerMaintenance,
  applyOutcomeBatch,
  datesInclusive,
  isActiveOn,
  monthEnd,
  monthStart,
  previewOwnerMaintenance,
  scoreDay,
  scoreTrail,
  undoOutcomeBatch,
  type MatrixDocument,
  type OwnerMaintenanceCommand,
  type OwnerMaintenancePreview as DomainOwnerMaintenancePreview,
  type OutcomeBatchPreview,
  weekStart,
  type SelectableOutcome,
  type TrailScore,
} from '../../packages/domain/src/index'
import { entryFor, useOwnerMatrix } from '../owner/OwnerMatrixContext'
import { useOffline } from '../offline'
import { currentRule, ScreenHeading } from './TodayScreen'
import { settingsHref } from './settingsRouting'
import { TimelineBatchControls, buildTimelineBatchPreview, type TimelineBatchPreviewResult, type TimelineBatchRequest, type TimelineBatchVariant } from '../components/TimelineBatchControls'
import {
  TimelineMaintenanceControls,
  type TimelineMaintenanceBackup,
  type TimelineMaintenanceConfirmation,
  type TimelineMaintenancePreview,
  type TimelineMaintenanceRequest,
  type TimelineMaintenanceTarget,
} from '../components/TimelineMaintenanceControls'
import { OwnerWorkflowError, supabaseOperationWorkflowAdapter } from '../features/operations/api'
import type { GeneralOutcomeBatchPreview, OwnerMaintenanceInput, OwnerMaintenancePreview, OwnerMaintenanceReceipt } from '../features/operations/contracts'
import {
  decodeOwnerMaintenanceRecovery,
  encodeOwnerMaintenanceRecovery,
  ownerMaintenanceRecoveryKey,
  type OwnerMaintenanceRecovery,
} from '../features/operations/ownerMaintenanceRecovery'
import {
  decodeTimelineBatchRecovery,
  encodeTimelineBatchRecovery,
  timelineBatchRecoveryKey,
  type TimelineBatchRecovery,
  type TimelineBatchKind,
} from '../features/operations/timelineBatchRecovery'

const ranges = ['7d', '14d', '28d', '90d', '12m'] as const
type Range = (typeof ranges)[number]
type Axis = 'daily' | 'weekly' | 'monthly'
interface Bucket { id: string; label: string; start: string; end: string; axis: Axis }
type TimelineDateState = 'past' | 'present' | 'future'
type TimelineSortColumn = 'operation' | '7d' | '28d'
type TimelineSortDirection = 'ascending' | 'descending'
interface TimelineSort { column: TimelineSortColumn; direction: TimelineSortDirection }

type HistoryNavigationKey = 'ArrowLeft' | 'ArrowRight' | 'ArrowUp' | 'ArrowDown' | 'Home' | 'End'

export function historyNavigationTarget(current: HTMLButtonElement, key: HistoryNavigationKey): HTMLButtonElement | null {
  const cell = current.closest('td')
  const row = cell?.parentElement as HTMLTableRowElement | null
  const table = row?.closest('table')
  if (!cell || !row || !table) return null
  const rows = Array.from(table.querySelectorAll<HTMLTableRowElement>('tbody > tr'))
  const rowIndex = rows.indexOf(row)
  const enabledInCell = (candidate: HTMLTableCellElement | undefined) => candidate?.querySelector<HTMLButtonElement>('button:not(:disabled)') ?? null
  const scanRow = (candidateRow: HTMLTableRowElement, from: number, step: -1 | 1) => {
    for (let index = from; index >= 1 && index < candidateRow.cells.length; index += step) {
      const target = enabledInCell(candidateRow.cells[index] as HTMLTableCellElement | undefined)
      if (target) return target
    }
    return null
  }
  if (key === 'ArrowLeft') return scanRow(row, cell.cellIndex - 1, -1)
  if (key === 'ArrowRight') return scanRow(row, cell.cellIndex + 1, 1)
  if (key === 'Home') return scanRow(row, 1, 1)
  if (key === 'End') return scanRow(row, row.cells.length - 1, -1)
  const step = key === 'ArrowUp' ? -1 : 1
  for (let index = rowIndex + step; index >= 0 && index < rows.length; index += step) {
    const target = enabledInCell(rows[index]?.cells[cell.cellIndex] as HTMLTableCellElement | undefined)
    if (target) return target
  }
  return null
}

export function rovingHistoryTabIndex(disabled: boolean, assigned: { current: boolean }): 0 | -1 {
  if (disabled || assigned.current) return -1
  assigned.current = true
  return 0
}

export function historyCellState(
  contribution: { eligible?: boolean; recordedOutsideSchedule?: boolean } | undefined,
  entry: { outcome: string } | undefined,
) {
  const inactive = contribution?.eligible !== true
  const recordedOutsideSchedule = contribution?.recordedOutsideSchedule === true || (inactive && entry !== undefined)
  return { inactive, recordedOutsideSchedule, disabled: inactive && !recordedOutsideSchedule }
}

export function timelineDateState(bucket: Pick<Bucket, 'start' | 'end'>, today: string): TimelineDateState {
  if (bucket.start > today) return 'future'
  if (bucket.end < today) return 'past'
  return 'present'
}

export function timelineCellPresentation(contribution: {
  displayOutcome: SelectableOutcome | null
  displaySource: 'stored' | 'generated-schedule' | 'required-blank' | 'unresolved-blank' | 'inactive-blank' | 'imported-hidden'
  projected: boolean
  recordedOutsideSchedule: boolean
}) {
  const code = contribution.displayOutcome
  const text = contribution.displaySource === 'inactive-blank' ? '' : code ?? '·'
  let description = code ?? 'blank'
  if (contribution.displaySource === 'generated-schedule') description = 'B, generated by schedule'
  if (contribution.displaySource === 'required-blank') description = 'blank, required'
  if (contribution.displaySource === 'inactive-blank') description = 'not scheduled'
  if (contribution.displaySource === 'imported-hidden') description = 'imported adjustment hidden'
  if (contribution.recordedOutsideSchedule) description = `${code ?? 'entry'}, recorded outside schedule and ignored in scores`
  if (contribution.projected) description = `${description}, projected`
  return { code, text, description }
}

function shortDay(date: string): string {
  return new Intl.DateTimeFormat('en-AU', { weekday: 'short', day: 'numeric', timeZone: 'UTC' }).format(new Date(`${date}T00:00:00Z`))
}

function monthOffset(date: string, amount: number): string {
  const instant = new Date(`${date}T00:00:00Z`)
  const normalized = new Date(Date.UTC(instant.getUTCFullYear(), instant.getUTCMonth() + amount, 1))
  return `${normalized.getUTCFullYear()}-${String(normalized.getUTCMonth() + 1).padStart(2, '0')}-01`
}

export function shiftTimelineAnchor(range: Range, anchor: string, direction: -1 | 1): string {
  if (range === '12m') return monthOffset(anchor, direction * 12)
  const days = range === '7d' ? 7 : range === '14d' ? 14 : range === '28d' ? 28 : 91
  return addDays(anchor, direction * days)
}

export function historyBuckets(range: Range, today: string): Bucket[] {
  if (range === '7d' || range === '14d' || range === '28d') {
    const days = range === '7d' ? 7 : range === '14d' ? 14 : 28
    return Array.from({ length: days }, (_, index) => {
      const date = addDays(today, index - (days - 1))
      return { id: date, label: shortDay(date), start: date, end: date, axis: 'daily' as const }
    })
  }
  if (range === '90d') {
    const currentWeek = weekStart(today)
    return Array.from({ length: 13 }, (_, index) => {
      const start = addDays(currentWeek, (index - 12) * 7)
      const end = addDays(start, 6) > today ? today : addDays(start, 6)
      return { id: start, label: `Wk ${shortDay(start)}`, start, end, axis: 'weekly' as const }
    })
  }
  const currentMonth = monthStart(today)
  return Array.from({ length: 12 }, (_, index) => {
    const start = monthOffset(currentMonth, index - 11)
    const naturalEnd = monthEnd(start)
    return {
      id: start,
      label: new Intl.DateTimeFormat('en-AU', { month: 'short', year: '2-digit', timeZone: 'UTC' }).format(new Date(`${start}T00:00:00Z`)),
      start,
      end: naturalEnd > today ? today : naturalEnd,
      axis: 'monthly' as const,
    }
  })
}

export function timelineBucketScore(
  document: MatrixDocument,
  bucket: Pick<Bucket, 'start' | 'end'>,
  asOf: string,
  operationIds?: readonly string[],
): number | null {
  const scores = datesInclusive(bucket.start, bucket.end)
    .map((date) => scoreDay(document, date, { ...(operationIds ? { operationIds } : {}), asOf }).score)
    .filter((score): score is number => score !== null)
  return scores.length ? scores.reduce((sum, score) => sum + score, 0) / scores.length : null
}

export function timelineOperationTrails(document: MatrixDocument, operationId: string, today: string): readonly [TrailScore, TrailScore] {
  const trail = (days: 7 | 28) => scoreTrail(
    document,
    { id: `${operationId}-${days}d`, label: `${days} day`, days },
    today,
    { operationIds: [operationId], asOf: today },
  )
  return [trail(7), trail(28)]
}

export function timelineSortOperations<T extends { id: string; name: string }>(
  operations: readonly T[],
  trailsById: ReadonlyMap<string, readonly [TrailScore, TrailScore]>,
  sort: TimelineSort | null,
): T[] {
  if (!sort) return [...operations]
  const direction = sort.direction === 'ascending' ? 1 : -1
  const trailIndex = sort.column === '7d' ? 0 : 1
  return operations.map((operation, index) => ({ operation, index })).sort((left, right) => {
    if (sort.column === 'operation') {
      const compared = left.operation.name.localeCompare(right.operation.name, 'en-AU', { sensitivity: 'base', numeric: true })
      return compared === 0 ? left.index - right.index : compared * direction
    }
    const leftScore = trailsById.get(left.operation.id)?.[trailIndex].score ?? null
    const rightScore = trailsById.get(right.operation.id)?.[trailIndex].score ?? null
    if (leftScore === null && rightScore === null) return left.index - right.index
    if (leftScore === null) return 1
    if (rightScore === null) return -1
    const compared = leftScore - rightScore
    return compared === 0 ? left.index - right.index : compared * direction
  }).map(({ operation }) => operation)
}

function TimelineMatrixColumns({ buckets }: { buckets: readonly Bucket[] }) {
  return <colgroup>
    <col className="timeline-operation-column" />
    <col className="timeline-trail-column" />
    <col className="timeline-trail-column" />
    {buckets.map((bucket) => <col className="timeline-date-column" key={bucket.id} />)}
  </colgroup>
}

function formatTimelineScore(score: number | null): string {
  return score === null ? '—' : String(Math.round(score))
}

function mutationId(): string {
  if (globalThis.crypto?.randomUUID) return globalThis.crypto.randomUUID()
  const bytes = globalThis.crypto?.getRandomValues?.(new Uint8Array(16)) ?? Uint8Array.from({ length: 16 }, () => Math.floor(Math.random() * 256))
  bytes[6] = (bytes[6]! & 0x0f) | 0x40
  bytes[8] = (bytes[8]! & 0x3f) | 0x80
  const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('')
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`
}

function batchItemLabel(count: number, batchKind: TimelineBatchKind): string {
  if (batchKind === 'bye') return count === 1 ? 'Bye' : 'Byes'
  return count === 1 ? 'Timeline cell' : 'Timeline cells'
}

export interface TimelineArchivedFilters {
  readonly includePeriodArchived: boolean
  readonly showAllArchived: boolean
}

function activeDuring(periods: readonly { from: string; toExclusive: string | null }[], from: string, to: string): boolean {
  return periods.some((period) => period.from <= to && (period.toExclusive === null || period.toExclusive > from))
}

export function timelineVisibleOperations(
  document: MatrixDocument,
  categoryId: string,
  today: string,
  visibleFrom: string,
  visibleTo: string,
  filters: TimelineArchivedFilters,
) {
  const category = document.categories.find((candidate) => candidate.id === categoryId)
  if (!category) return []
  return category.operationOrder.flatMap((id) => {
    const operation = document.operations.find((candidate) => candidate.id === id)
    if (!operation) return []
    const currentlyActive = isActiveOn(operation.activePeriods, today)
    if (currentlyActive || filters.showAllArchived || filters.includePeriodArchived && activeDuring(operation.activePeriods, visibleFrom, visibleTo)) {
      return [{ operation, currentlyActive }]
    }
    return []
  })
}

function localIdBySourceKey(document: MatrixDocument): Map<string, string> {
  return new Map([
    ...document.categories.map((category) => [category.sourceKey ?? category.id, category.id] as const),
    ...document.operations.map((operation) => [operation.sourceKey ?? operation.id, operation.id] as const),
  ])
}

export function canonicalMaintenanceBackupJson(value: unknown): string {
  if (value === null || typeof value !== 'object') return JSON.stringify(value) ?? 'undefined'
  if (Array.isArray(value)) return `[${value.map(canonicalMaintenanceBackupJson).join(',')}]`
  const record = value as Readonly<Record<string, unknown>>
  return `{${Object.keys(record).sort().map((key) => `${JSON.stringify(key)}:${canonicalMaintenanceBackupJson(record[key])}`).join(',')}}`
}

function maintenanceImpactRows(preview: Pick<OwnerMaintenancePreview, 'affectedItems' | 'affectedEntryCount' | 'impact' | 'categoryImpacts'>) {
  const { impact } = preview
  const scoreSnapshot = (snapshot: { score: number | null; numerator: number; denominator: number }) => snapshot.score === null
    ? `No score (${snapshot.numerator}/${snapshot.denominator})`
    : `${snapshot.score} points (${snapshot.numerator}/${snapshot.denominator})`
  const todayBefore = impact.today.before === null ? 'No score' : `${Math.round(impact.today.before)} points`
  const todayAfter = impact.today.after === null ? 'No score' : `${Math.round(impact.today.after)} points`
  return [
    { label: 'Affected items', value: String(preview.affectedItems.length) },
    { label: 'Affected recorded entries', value: String(preview.affectedEntryCount) },
    { label: 'Score days changed', value: String(impact.changedDays.length) },
    { label: 'Today score', value: `${todayBefore} to ${todayAfter}` },
    { label: 'Trail values changed', value: String(impact.trails.filter((trail) => trail.before !== trail.after).length) },
    { label: 'Accumulation change', value: `${impact.accumulation.delta >= 0 ? '+' : ''}${impact.accumulation.delta}` },
    { label: 'Entries outside schedule', value: String(impact.newlyOutsideScheduleEntries.length) },
    { label: 'Entries newly included', value: String(impact.newlyIncludedEntries.length) },
    ...impact.changedDays.map((day) => ({
      label: `${day.date} overall score`, value: `${scoreSnapshot(day.before)} to ${scoreSnapshot(day.after)}`,
    })),
    ...impact.trails.filter((trail) => trail.before !== trail.after).map((trail) => ({
      label: `${trail.days}-day trail`, value: `${trail.before ?? 'No score'} to ${trail.after ?? 'No score'}`,
    })),
    { label: 'Accumulation', value: `${impact.accumulation.before} to ${impact.accumulation.after}` },
    ...impact.newlyOutsideScheduleEntries.map((entry) => ({
      label: `${entry.date} moved outside schedule`, value: `${entry.operationId}: ${entry.outcome}`,
    })),
    ...impact.newlyIncludedEntries.map((entry) => ({
      label: `${entry.date} newly included`, value: `${entry.operationId}: ${entry.outcome}`,
    })),
    ...preview.categoryImpacts.map((category) => ({
      label: `${category.categoryName} score days changed`,
      value: String(category.impact.changedDays.length),
    })),
    ...preview.categoryImpacts.flatMap((category) => category.impact.changedDays.map((day) => ({
      label: `${category.categoryName}, ${day.date}`,
      value: `${scoreSnapshot(day.before)} to ${scoreSnapshot(day.after)}`,
    }))),
  ]
}

function componentRequestFromResolved(
  original: TimelineMaintenanceRequest,
  preview: OwnerMaintenancePreview,
  localBySource: ReadonlyMap<string, string>,
): TimelineMaintenanceRequest {
  if (original.action !== 'restore') return original
  const operationIds = preview.input.restoreOperationSourceKeys.map((sourceKey) => localBySource.get(sourceKey)).filter((id): id is string => id !== undefined)
  return { ...original, operationIds }
}

function componentPreviewFromRemote(
  original: TimelineMaintenanceRequest,
  preview: OwnerMaintenancePreview,
  localBySource: ReadonlyMap<string, string>,
): TimelineMaintenancePreview {
  const affectedOperations = preview.affectedItems.filter((item) => item.kind === 'operation').map((item) => ({
    id: localBySource.get(item.sourceKey) ?? item.sourceKey,
    name: item.name,
  }))
  return {
    request: componentRequestFromResolved(original, preview, localBySource),
    impact: maintenanceImpactRows(preview),
    affectedOperations,
    warnings: original.action === 'delete'
      ? ['Permanent Delete removes affected history and recalculates scores. The audit retains only a redacted tombstone.']
      : [],
    backup: preview.backup ? {
      filename: preview.backup.fileName,
      content: canonicalMaintenanceBackupJson(preview.backup.document),
      sha256: preview.backup.sha256,
    } : undefined,
  }
}

function componentPreviewFromDomain(
  request: TimelineMaintenanceRequest,
  preview: DomainOwnerMaintenancePreview,
  document: MatrixDocument,
): TimelineMaintenancePreview {
  const affectedOperations = preview.affectedOperationIds.flatMap((id) => {
    const operation = document.operations.find((candidate) => candidate.id === id)
    return operation ? [{ id, name: operation.name }] : []
  })
  const affectedItems = [
    ...preview.affectedCategoryIds.map((id) => ({ kind: 'category' as const, sourceKey: id, name: document.categories.find((category) => category.id === id)?.name ?? id, change: request.action })),
    ...preview.affectedOperationIds.map((id) => ({ kind: 'operation' as const, sourceKey: id, name: document.operations.find((operation) => operation.id === id)?.name ?? id, change: request.action })),
  ]
  const normalizedRequest = request.action === 'restore' && request.target.kind === 'category'
    ? { ...request, operationIds: preview.affectedOperationIds }
    : request
  return {
    request: normalizedRequest,
    impact: maintenanceImpactRows({
      affectedItems, affectedEntryCount: preview.counts.affectedEntries, impact: preview.impact,
      categoryImpacts: preview.categoryImpacts.map((categoryImpact) => ({
        categorySourceKey: categoryImpact.categoryId,
        categoryName: document.categories.find((category) => category.id === categoryImpact.categoryId)?.name ?? categoryImpact.categoryId,
        impact: categoryImpact.impact,
      })),
    }),
    affectedOperations,
    warnings: request.action === 'delete'
      ? ['Permanent Delete removes affected history and recalculates scores. The audit retains only a redacted tombstone.']
      : [],
    backup: request.action === 'delete' ? {
      filename: `ops-matrix-complete-backup-${document.ownerId}.json`,
      content: JSON.stringify(document, null, 2),
      sha256: '0'.repeat(64),
    } : undefined,
  }
}

function maintenanceInputFromRequest(document: MatrixDocument, request: TimelineMaintenanceRequest): OwnerMaintenanceInput {
  const sourceForTarget = request.target.kind === 'category'
    ? document.categories.find((category) => category.id === request.target.id)?.sourceKey
    : document.operations.find((operation) => operation.id === request.target.id)?.sourceKey
  const targetSourceKey = sourceForTarget ?? request.target.id
  if (request.action === 'rename') return { action: 'rename', targetKind: request.target.kind, targetSourceKey, newName: request.name }
  if (request.action === 'move') {
    const destination = document.categories.find((category) => category.id === request.categoryId)
    return { action: 'move', targetKind: 'operation', targetSourceKey, destinationCategorySourceKey: destination?.sourceKey ?? request.categoryId }
  }
  if (request.action === 'archive') return { action: 'archive', targetKind: request.target.kind, targetSourceKey, effectiveDate: request.effectiveFrom }
  if (request.action === 'restore') return {
    action: 'restore', targetKind: request.target.kind, targetSourceKey, effectiveDate: request.effectiveFrom,
    ...(request.operationIds ? { restoreOperationSourceKeys: request.operationIds.map((id) => document.operations.find((operation) => operation.id === id)?.sourceKey ?? id) } : {}),
  }
  return { action: 'delete', targetKind: request.target.kind, targetSourceKey }
}

function domainCommandFromRequest(
  document: MatrixDocument,
  request: TimelineMaintenanceRequest,
  cascadeDefaults: ReadonlyMap<string, readonly string[]>,
): OwnerMaintenanceCommand {
  if (request.action === 'rename') return request.target.kind === 'operation'
    ? { kind: 'rename-operation', operationId: request.target.id, name: request.name }
    : { kind: 'rename-category', categoryId: request.target.id, name: request.name }
  if (request.action === 'move') return { kind: 'move-operation', operationId: request.target.id, categoryId: request.categoryId }
  if (request.action === 'archive') return request.target.kind === 'operation'
    ? { kind: 'archive-operation', operationId: request.target.id, effectiveFrom: request.effectiveFrom }
    : { kind: 'archive-category', categoryId: request.target.id, effectiveFrom: request.effectiveFrom }
  if (request.action === 'restore') {
    if (request.target.kind === 'operation') return { kind: 'restore-operation', operationId: request.target.id, effectiveFrom: request.effectiveFrom }
    const categoryOperationIds = document.operations.filter((operation) => operation.categoryId === request.target.id).map((operation) => operation.id)
    const cascadeOperationIds = [...(cascadeDefaults.get(request.target.id) ?? categoryOperationIds.filter((id) => {
      const operation = document.operations.find((candidate) => candidate.id === id)!
      return !isActiveOn(operation.activePeriods, request.effectiveFrom)
    }))]
    const selected = request.operationIds ?? cascadeOperationIds
    return {
      kind: 'restore-category', categoryId: request.target.id, effectiveFrom: request.effectiveFrom,
      cascadeOperationIds, deselectedOperationIds: cascadeOperationIds.filter((id) => !selected.includes(id)),
    }
  }
  return request.target.kind === 'operation'
    ? { kind: 'delete-operation', operationId: request.target.id }
    : { kind: 'delete-category', categoryId: request.target.id }
}

export function downloadTimelineMaintenanceBackup(backup: TimelineMaintenanceBackup): void {
  const url = URL.createObjectURL(new Blob([backup.content], { type: 'application/json' }))
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = backup.filename
  anchor.click()
  window.setTimeout(() => URL.revokeObjectURL(url), 0)
}

export function remoteTimelineBatchResult(
  preview: GeneralOutcomeBatchPreview,
  localIdsBySourceKey: ReadonlyMap<string, string>,
): TimelineBatchPreviewResult {
  const resolveLocalId = (sourceKey: string) => {
    const localId = localIdsBySourceKey.get(sourceKey)
    if (!localId) throw new Error(`The authoritative batch referenced unknown operation ${sourceKey}. Reload before retrying.`)
    return localId
  }
  const localOperationIds = preview.input.operationIds.map(resolveLocalId)
  const domainPreview: OutcomeBatchPreview = {
    input: { ...preview.input, operationIds: localOperationIds },
    cells: preview.cells.map((cell) => ({
      operationId: resolveLocalId(cell.operationSourceKey),
      date: cell.date,
      before: cell.before,
      after: cell.after,
    })),
    beforeFingerprint: `server:${preview.expectedStructureVersion}:${preview.expectedScoreRevision}:${preview.previewFingerprint}`,
    afterFingerprint: `server-result:${preview.previewFingerprint}`,
    skippedExisting: preview.counts.skippedExisting,
    skippedEmpty: preview.counts.skippedEmpty,
    skippedGeneratedBye: preview.counts.skippedGeneratedBye,
    skippedImportOnly: preview.counts.skippedImportOnly,
    skippedNotDue: preview.counts.skippedNotDue,
    skippedInactive: preview.counts.skippedInactive,
    skippedWeekday: preview.counts.skippedWeekday,
  }
  return {
    preview: domainPreview,
    note: preview.input.note?.trim() || null,
    counts: {
      changed: preview.counts.changed,
      protected: preview.counts.skippedExisting + preview.counts.skippedImportOnly + preview.counts.skippedGeneratedBye,
      skipped: preview.counts.skippedEmpty + preview.counts.skippedNotDue + preview.counts.skippedInactive + preview.counts.skippedWeekday,
    },
    currentScoreImpact: {
      before: { score: preview.impact.today.before },
      after: { score: preview.impact.today.after },
    },
    historicalImpact: preview.impact,
  }
}

export function timelineBucketSpan(buckets: readonly Pick<Bucket, 'start' | 'end'>[], fallback: string) {
  if (buckets.length === 0) return { from: fallback, to: fallback }
  return {
    from: buckets.reduce((earliest, bucket) => bucket.start < earliest ? bucket.start : earliest, buckets[0]!.start),
    to: buckets.reduce((latest, bucket) => bucket.end > latest ? bucket.end : latest, buckets[0]!.end),
  }
}

export function timelineOperationRange(order: readonly string[], firstId: string, lastId: string): readonly string[] {
  const first = order.indexOf(firstId)
  const last = order.indexOf(lastId)
  if (first < 0 || last < 0) return []
  return order.slice(Math.min(first, last), Math.max(first, last) + 1)
}

export function TimelineScreen() {
  const location = useLocation()
  const {
    document, trustedToday, mode, structureSync, securedBatchBlocker,
    accumulationAnchor, replaceDocument,
    setOutcome, commitEntryBatch, refreshRevision, reloadAuthoritativeStructure,
    assertSecuredBatchReady, securedBatchSnapshot,
  } = useOwnerMatrix()
  const { recordEntry, online, snapshot, readWorkflowRecovery, writeWorkflowRecovery } = useOffline()
  const [range, setRange] = useState<Range>('28d')
  const [drill, setDrill] = useState<Bucket | null>(null)
  const [focusEnd, setFocusEnd] = useState<string | null>(null)
  const [operationSort, setOperationSort] = useState<TimelineSort | null>(null)
  const [jumpDate, setJumpDate] = useState(trustedToday)
  const [selectMode, setSelectMode] = useState(false)
  const [batchVariant, setBatchVariant] = useState<TimelineBatchVariant>('general')
  const [selectedOperationIds, setSelectedOperationIds] = useState<readonly string[]>([])
  const [selectionFrom, setSelectionFrom] = useState(trustedToday)
  const [selectionTo, setSelectionTo] = useState(trustedToday)
  const [previewResult, setPreviewResult] = useState<TimelineBatchPreviewResult | null>(null)
  const [batchStatus, setBatchStatus] = useState('')
  const [undoBusy, setUndoBusy] = useState(false)
  const [batchBusy, setBatchBusy] = useState(false)
  const [includePeriodArchived, setIncludePeriodArchived] = useState(false)
  const [showAllArchived, setShowAllArchived] = useState(false)
  const [showByeSources, setShowByeSources] = useState(false)
  const [maintenanceStatus, setMaintenanceStatus] = useState('')
  const [maintenanceBusy, setMaintenanceBusy] = useState(false)
  const [maintenanceConflict, setMaintenanceConflict] = useState<string | null>(null)
  const [durableMaintenanceRecovery, setDurableMaintenanceRecovery] = useState<OwnerMaintenanceRecovery | null>(null)
  const [durableBatchRecovery, setDurableBatchRecovery] = useState<TimelineBatchRecovery | null>(null)
  const [durableBatchBusy, setDurableBatchBusy] = useState(false)
  const [dragActive, setDragActive] = useState(false)
  const [reloadRecovery, setReloadRecovery] = useState<{ kind: 'apply' | 'undo'; message: string; changed: number; batchKind: TimelineBatchKind } | null>(null)
  const [reloadBusy, setReloadBusy] = useState(false)
  const [undoState, setUndoState] = useState<
    | { kind: 'synthetic'; preview: OutcomeBatchPreview; changed: number; batchKind: 'bye' | 'timeline' }
    | { kind: 'remote'; batchId: string; changed: number; undoMutationId: string; batchKind: 'bye' | 'timeline' }
    | null
  >(null)
  const previousRange = useRef<Range>('28d')
  const scrollers = useRef(new Set<HTMLDivElement>())
  const synchronizedScrollLeft = useRef<number | null>(null)
  const selectButton = useRef<HTMLButtonElement>(null)
  const undoButton = useRef<HTMLButtonElement>(null)
  const selectionAnchor = useRef<{ operationId: string; date: string } | null>(null)
  const dragAnchor = useRef<{ operationId: string; date: string } | null>(null)
  const dragging = useRef(false)
  const remotePreview = useRef<GeneralOutcomeBatchPreview | null>(null)
  const remotePreviewByResult = useRef(new WeakMap<TimelineBatchPreviewResult, GeneralOutcomeBatchPreview>())
  const clientBatchId = useRef<string | null>(null)
  const batchRecoveryGeneration = useRef(0)
  const remoteMaintenanceByResult = useRef(new WeakMap<TimelineMaintenancePreview, OwnerMaintenancePreview>())
  const maintenanceMutationByResult = useRef(new WeakMap<TimelineMaintenancePreview, string>())
  const syntheticMaintenanceByResult = useRef(new WeakMap<TimelineMaintenancePreview, DomainOwnerMaintenancePreview>())
  const syntheticCategoryCascade = useRef(new Map<string, readonly string[]>())
  const buckets = useMemo(() => drill
    ? datesInclusive(drill.start, drill.end).map((date) => ({ id: date, label: shortDay(date), start: date, end: date, axis: 'daily' as const }))
    : historyBuckets(range, focusEnd ?? trustedToday), [drill, focusEnd, range, trustedToday])
  const operationTrailsById = useMemo(() => new Map(document.operations.map((operation) => [
    operation.id,
    timelineOperationTrails(document, operation.id, trustedToday),
  ])), [document, trustedToday])
  const operationOrder = useMemo(() => document.categories.flatMap((category) => category.operationOrder)
    .filter((id, index, ids) => ids.indexOf(id) === index && document.operations.some((operation) => operation.id === id)), [document])
  const selectedCells = useMemo(() => new Set(datesInclusive(selectionFrom, selectionTo)
    .flatMap((date) => selectedOperationIds.map((operationId) => `${operationId}\u0000${date}`))), [selectedOperationIds, selectionFrom, selectionTo])
  const previewCells = useMemo(() => new Set(previewResult?.preview.cells.map((cell) => `${cell.operationId}\u0000${cell.date}`) ?? []), [previewResult])

  useLayoutEffect(() => {
    const rightEdge = Array.from(scrollers.current).reduce((furthest, scroller) => Math.max(furthest, scroller.scrollWidth - scroller.clientWidth), 0)
    synchronizedScrollLeft.current = rightEdge
    for (const scroller of scrollers.current) scroller.scrollLeft = rightEdge
  }, [buckets, showByeSources])

  useEffect(() => {
    let cancelled = false
    if (mode !== 'remote-owner') { setDurableMaintenanceRecovery(null); return }
    void readWorkflowRecovery(ownerMaintenanceRecoveryKey(document.ownerId)).then((raw) => {
      if (cancelled) return
      const recovery = decodeOwnerMaintenanceRecovery(raw, document.ownerId)
      setDurableMaintenanceRecovery(recovery)
      if (raw && !recovery) void writeWorkflowRecovery(ownerMaintenanceRecoveryKey(document.ownerId), null)
    })
    return () => { cancelled = true }
  }, [document.ownerId, mode, readWorkflowRecovery, writeWorkflowRecovery])

  useEffect(() => {
    let cancelled = false
    const generation = batchRecoveryGeneration.current
    if (mode !== 'remote-owner') { setDurableBatchRecovery(null); return }
    setDurableBatchRecovery(null)
    setReloadRecovery(null)
    setUndoState((current) => current?.kind === 'remote' ? null : current)
    void readWorkflowRecovery(timelineBatchRecoveryKey(document.ownerId)).then((raw) => {
      if (cancelled || generation !== batchRecoveryGeneration.current) return
      const recovery = decodeTimelineBatchRecovery(raw, document.ownerId)
      setDurableBatchRecovery(recovery)
      if (raw && !recovery) {
        batchRecoveryGeneration.current += 1
        void writeWorkflowRecovery(timelineBatchRecoveryKey(document.ownerId), null)
        return
      }
      if (!recovery) return
      if (recovery.phase === 'reload-after-apply') {
        setReloadRecovery({ kind: 'apply', message: 'The batch committed. Load authoritative owner data before Undo.', changed: recovery.changed, batchKind: recovery.batchKind })
        setBatchStatus(`${recovery.changed} ${batchItemLabel(recovery.changed, recovery.batchKind)} changed. Authoritative reload is required.`)
      } else if (recovery.phase === 'undo-available' || recovery.phase === 'undo-pending') {
        setUndoState({ kind: 'remote', batchId: recovery.batchId, changed: recovery.changed, undoMutationId: recovery.undoMutationId, batchKind: recovery.batchKind })
        setBatchStatus(recovery.phase === 'undo-pending'
          ? 'The Undo response was interrupted. Retry uses the saved request identity.'
          : `${recovery.changed} ${batchItemLabel(recovery.changed, recovery.batchKind)} changed. Whole-batch Undo is available.`)
      } else if (recovery.phase === 'reload-after-undo') {
        setReloadRecovery({ kind: 'undo', message: 'Undo committed. Load authoritative owner data to finish recovery.', changed: recovery.changed, batchKind: recovery.batchKind })
        setBatchStatus('Undo committed. Authoritative reload is required.')
      } else {
        clientBatchId.current = recovery.clientBatchId
        setBatchStatus('The batch response was interrupted. Retry uses the saved request identity.')
      }
    })
    return () => { cancelled = true }
  }, [document.ownerId, mode, readWorkflowRecovery, writeWorkflowRecovery])

  const clearPreview = () => {
    setPreviewResult(null)
    remotePreview.current = null
    clientBatchId.current = null
  }

  const closeSelection = () => {
    if (batchBusy) return
    setSelectMode(false)
    setSelectedOperationIds([])
    setBatchVariant('general')
    selectionAnchor.current = null
    clearPreview()
    window.setTimeout(() => selectButton.current?.focus(), 0)
  }

  useEffect(() => {
    const release = () => { dragging.current = false; dragAnchor.current = null; setDragActive(false) }
    const escape = (event: globalThis.KeyboardEvent) => {
      if (event.key !== 'Escape' || !selectMode || batchBusy) return
      event.preventDefault()
      closeSelection()
    }
    window.addEventListener('pointerup', release)
    window.addEventListener('pointercancel', release)
    window.addEventListener('keydown', escape)
    return () => { window.removeEventListener('pointerup', release); window.removeEventListener('pointercancel', release); window.removeEventListener('keydown', escape) }
  }, [batchBusy, selectMode])

  const jump = (event: FormEvent) => {
    event.preventDefault()
    if (!jumpDate) return
    previousRange.current = range
    if (range === '90d' || range === '12m') setRange('14d')
    setDrill(null); setFocusEnd(jumpDate)
  }
  const backToToday = () => { setFocusEnd(null); setDrill(null); setRange(previousRange.current) }
  const moveTimeline = (direction: -1 | 1) => {
    previousRange.current = range
    setDrill(null)
    setFocusEnd(shiftTimelineAnchor(range, focusEnd ?? trustedToday, direction))
  }
  const changeOperationSort = (column: TimelineSortColumn) => {
    setOperationSort((current) => {
      const initialDirection: TimelineSortDirection = column === 'operation' ? 'ascending' : 'descending'
      if (current?.column !== column) return { column, direction: initialDirection }
      if (current.direction === initialDirection) return { column, direction: initialDirection === 'ascending' ? 'descending' : 'ascending' }
      return null
    })
  }

  const cycle = (operationId: string, date: string) => {
    const current = entryFor(document, operationId, date)?.outcome ?? null
    const order: Array<SelectableOutcome | null> = [null, ...SELECTABLE_OUTCOMES]
    const next = current === 'M' ? null : order[(order.indexOf(current) + 1) % order.length]
    setOutcome(operationId, date, next)
    const sourceKey = document.operations.find((operation) => operation.id === operationId)?.sourceKey ?? operationId
    void recordEntry({ ownerId: document.ownerId, operationId: sourceKey, date, outcome: next, deleted: next === null })
  }

  const selectRectangle = (anchor: { operationId: string; date: string }, target: { operationId: string; date: string }) => {
    if (batchBusy) return
    const operationIds = timelineOperationRange(operationOrder, anchor.operationId, target.operationId)
    if (operationIds.length === 0) return
    setSelectedOperationIds(operationIds)
    setSelectionFrom(anchor.date < target.date ? anchor.date : target.date)
    setSelectionTo(anchor.date > target.date ? anchor.date : target.date)
    clearPreview()
  }

  const selectCell = (operationId: string, date: string, extend: boolean) => {
    if (batchBusy) return
    const target = { operationId, date }
    if (extend && selectionAnchor.current) selectRectangle(selectionAnchor.current, target)
    else {
      selectionAnchor.current = target
      setSelectedOperationIds([operationId])
      setSelectionFrom(date)
      setSelectionTo(date)
      clearPreview()
    }
  }

  const startDrag = (event: PointerEvent<HTMLButtonElement>, operationId: string, date: string) => {
    if (!selectMode || batchBusy || event.pointerType !== 'mouse' || event.button !== 0) return
    const target = { operationId, date }
    dragging.current = true
    setDragActive(true)
    dragAnchor.current = event.shiftKey && selectionAnchor.current ? selectionAnchor.current : target
    if (!event.shiftKey) selectionAnchor.current = target
    selectRectangle(dragAnchor.current, target)
  }

  const extendDrag = (operationId: string, date: string) => {
    if (selectMode && !batchBusy && dragging.current && dragAnchor.current) selectRectangle(dragAnchor.current, { operationId, date })
  }

  const selectOperations = (operationIds: readonly string[], selectedBuckets: readonly Bucket[] = buckets) => {
    if (batchBusy) return
    const span = timelineBucketSpan(selectedBuckets, trustedToday)
    setSelectedOperationIds(operationIds)
    setSelectionFrom(span.from)
    setSelectionTo(span.to)
    selectionAnchor.current = operationIds[0] ? { operationId: operationIds[0], date: span.from } : null
    clearPreview()
  }

  const batchGuard = () => {
    if (mode === 'remote-owner') {
      if (!online) throw new Error('Batch changes require an internet connection.')
      if (snapshot.pending > 0 || snapshot.phase !== 'synced') throw new Error('Wait for queued entry changes to finish syncing.')
      if (structureSync.phase !== 'synced') throw new Error('Wait for structural changes to finish syncing.')
      if (securedBatchBlocker) throw new Error(securedBatchBlocker)
    }
    assertSecuredBatchReady()
  }

  const provideBatchPreview = async (input: TimelineBatchRequest): Promise<TimelineBatchPreviewResult> => {
    batchGuard()
    if (mode === 'synthetic-preview') return buildTimelineBatchPreview(document, trustedToday, input)
    await refreshRevision()
    batchGuard()
    const authoritative = securedBatchSnapshot()
    const sourceByLocalId = new Map(authoritative.document.operations.map((operation) => [operation.id, operation.sourceKey ?? operation.id]))
    const localBySourceKey = new Map(authoritative.document.operations.map((operation) => [operation.sourceKey ?? operation.id, operation.id]))
    const sourceOperationIds = input.operationIds.map((operationId) => {
      const sourceKey = sourceByLocalId.get(operationId)
      if (!sourceKey) throw new Error(`Selected operation ${operationId} is absent from authoritative owner data. Reload before retrying.`)
      return sourceKey
    })
    const serverPreview = await supabaseOperationWorkflowAdapter.previewOutcomeBatch(authoritative.revision, {
      ...input,
      operationIds: sourceOperationIds,
      weekdays: input.weekdays ? [...input.weekdays] : null,
    })
    batchGuard()
    const afterPreview = securedBatchSnapshot()
    if (serverPreview.expectedStructureVersion !== afterPreview.revision.structureVersion || serverPreview.expectedScoreRevision !== afterPreview.revision.scoreRevision) {
      throw new Error('Owner revision changed while previewing. Review the batch again.')
    }
    const result = remoteTimelineBatchResult(serverPreview, localBySourceKey)
    remotePreviewByResult.current.set(result, serverPreview)
    return result
  }

  const storeBatchRecovery = async (recovery: TimelineBatchRecovery) => {
    batchRecoveryGeneration.current += 1
    await writeWorkflowRecovery(timelineBatchRecoveryKey(recovery.ownerId), encodeTimelineBatchRecovery(recovery))
    setDurableBatchRecovery(recovery)
  }

  const discardBatchRecovery = async (ownerId: string) => {
    batchRecoveryGeneration.current += 1
    await writeWorkflowRecovery(timelineBatchRecoveryKey(ownerId), null)
    setDurableBatchRecovery(null)
    clientBatchId.current = null
  }

  const finishCommittedBatchReload = async (recovery: TimelineBatchRecovery) => {
    if (recovery.phase !== 'reload-after-apply' && recovery.phase !== 'reload-after-undo') return
    const kind = recovery.phase === 'reload-after-apply' ? 'apply' : 'undo'
    setReloadRecovery({
      kind,
      message: kind === 'apply' ? 'Reloading authoritative owner data after the committed batch.' : 'Reloading authoritative owner data after the committed Undo.',
      changed: recovery.changed,
      batchKind: recovery.batchKind,
    })
    try {
      batchGuard()
      await reloadAuthoritativeStructure()
    } catch (caught) {
      const message = caught instanceof Error ? caught.message : 'Authoritative owner data could not be reloaded.'
      setReloadRecovery({ kind, message, changed: recovery.changed, batchKind: recovery.batchKind })
      throw new Error(`${kind === 'apply' ? 'The batch' : 'The Undo'} committed, but authoritative reload failed. Retry reload. ${message}`)
    }
    if (recovery.phase === 'reload-after-apply') {
      const available: TimelineBatchRecovery = { ...recovery, phase: 'undo-available', createdAt: new Date().toISOString() }
      await storeBatchRecovery(available)
      setUndoState({ kind: 'remote', batchId: available.batchId, changed: available.changed, undoMutationId: available.undoMutationId, batchKind: available.batchKind })
      setBatchStatus(`${available.changed} ${batchItemLabel(available.changed, available.batchKind)} changed. Whole-batch Undo is available.`)
      clearPreview()
      setSelectMode(false)
      setSelectedOperationIds([])
      setBatchVariant('general')
      window.setTimeout(() => undoButton.current?.focus(), 0)
    } else {
      await discardBatchRecovery(recovery.ownerId)
      setUndoState(null)
      setBatchStatus(`${recovery.changed} ${batchItemLabel(recovery.changed, recovery.batchKind)} restored.`)
    }
    setReloadRecovery(null)
  }

  const applySavedBatch = async (recovery: Extract<TimelineBatchRecovery, { phase: 'apply-pending' }>) => {
    batchGuard()
    const receipt = await supabaseOperationWorkflowAdapter.applyOutcomeBatch(recovery.preview, recovery.clientBatchId)
    const committed: TimelineBatchRecovery = {
      version: 1,
      ownerId: recovery.ownerId,
      batchKind: recovery.batchKind,
      createdAt: new Date().toISOString(),
      phase: 'reload-after-apply',
      batchId: receipt.batchId,
      changed: receipt.changedCells,
      undoMutationId: mutationId(),
    }
    await storeBatchRecovery(committed)
    setUndoState(null)
    await finishCommittedBatchReload(committed)
  }

  const undoSavedBatch = async (recovery: Extract<TimelineBatchRecovery, { phase: 'undo-pending' }>) => {
    await refreshRevision()
    batchGuard()
    const authoritative = securedBatchSnapshot()
    const receipt = await supabaseOperationWorkflowAdapter.undoOutcomeBatch(authoritative.revision, recovery.batchId, recovery.undoMutationId)
    const committed: TimelineBatchRecovery = {
      version: 1,
      ownerId: recovery.ownerId,
      batchKind: recovery.batchKind,
      createdAt: new Date().toISOString(),
      phase: 'reload-after-undo',
      batchId: receipt.batchId,
      changed: receipt.undoneCells,
    }
    await storeBatchRecovery(committed)
    setUndoState(null)
    await finishCommittedBatchReload(committed)
  }

  const clearDefiniteBatchConflict = async (caught: unknown, ownerId: string) => {
    if (!(caught instanceof OwnerWorkflowError) || caught.status !== 409) return false
    await discardBatchRecovery(ownerId)
    setReloadRecovery(null)
    setUndoState(null)
    clearPreview()
    return true
  }

  const applyBatch = async (result: TimelineBatchPreviewResult) => {
    batchGuard()
    const batchKind = batchVariant === 'bye-period' ? 'bye' : 'timeline'
    if (mode === 'synthetic-preview') {
      const note = result.note ? `: ${result.note}` : ''
      const label = batchVariant === 'bye-period' ? 'Bye period' : 'Timeline batch'
      commitEntryBatch((current) => applyOutcomeBatch(current, result.preview), `Applied ${result.counts.changed}-cell ${label}${note}`)
      setUndoState({ kind: 'synthetic', preview: result.preview, changed: result.counts.changed, batchKind })
      setBatchStatus(`${result.counts.changed} ${batchItemLabel(result.counts.changed, batchKind)} changed. Whole-batch Undo is available.`)
      clearPreview()
      setSelectMode(false)
      setSelectedOperationIds([])
      setBatchVariant('general')
      window.setTimeout(() => undoButton.current?.focus(), 0)
      return
    }
    const secured = remotePreview.current
    if (!secured) throw new Error('Review the exact server preview before applying.')
    const current = securedBatchSnapshot()
    if (current.revision.structureVersion !== secured.expectedStructureVersion || current.revision.scoreRevision !== secured.expectedScoreRevision) {
      throw new Error('Owner revision changed after preview. Review the batch again.')
    }
    clientBatchId.current ??= mutationId()
    const pending: TimelineBatchRecovery = {
      version: 1,
      ownerId: document.ownerId,
      batchKind,
      createdAt: new Date().toISOString(),
      phase: 'apply-pending',
      preview: secured,
      clientBatchId: clientBatchId.current,
    }
    await storeBatchRecovery(pending)
    try {
      await applySavedBatch(pending)
    } catch (caught) {
      await clearDefiniteBatchConflict(caught, pending.ownerId)
      throw caught
    }
  }

  const undoBatch = async () => {
    if (!undoState) return
    batchGuard()
    if (undoState.kind === 'synthetic') {
      commitEntryBatch((current) => undoOutcomeBatch(current, undoState.preview), `Undid ${undoState.changed}-cell Timeline batch`)
      setBatchStatus(`${undoState.changed} ${batchItemLabel(undoState.changed, undoState.batchKind)} restored.`)
      setUndoState(null)
      return
    }
    const pending: TimelineBatchRecovery = {
      version: 1,
      ownerId: document.ownerId,
      batchKind: undoState.batchKind,
      createdAt: new Date().toISOString(),
      phase: 'undo-pending',
      batchId: undoState.batchId,
      changed: undoState.changed,
      undoMutationId: undoState.undoMutationId,
    }
    await storeBatchRecovery(pending)
    try {
      await undoSavedBatch(pending)
    } catch (caught) {
      await clearDefiniteBatchConflict(caught, pending.ownerId)
      throw caught
    }
  }

  const retryAuthoritativeReload = async () => {
    if (!durableBatchRecovery || durableBatchRecovery.phase !== 'reload-after-apply' && durableBatchRecovery.phase !== 'reload-after-undo') return
    setReloadBusy(true)
    try {
      await finishCommittedBatchReload(durableBatchRecovery)
    } catch (caught) {
      const message = caught instanceof Error ? caught.message : 'Authoritative owner data could not be reloaded.'
      setReloadRecovery((current) => current ? { ...current, message } : current)
    } finally {
      setReloadBusy(false)
    }
  }

  const retrySavedBatchRecovery = async () => {
    if (!durableBatchRecovery || durableBatchRecovery.phase !== 'apply-pending' && durableBatchRecovery.phase !== 'undo-pending') return
    const recovery = durableBatchRecovery
    setDurableBatchBusy(true)
    try {
      if (recovery.phase === 'apply-pending') await applySavedBatch(recovery)
      else await undoSavedBatch(recovery)
    } catch (caught) {
      await clearDefiniteBatchConflict(caught, recovery.ownerId)
      setBatchStatus(caught instanceof Error ? caught.message : 'The saved batch request could not be recovered.')
    } finally {
      setDurableBatchBusy(false)
    }
  }

  const requestUndo = async () => {
    setUndoBusy(true)
    try {
      await undoBatch()
    } catch (caught) {
      setBatchStatus(caught instanceof Error ? caught.message : 'The whole batch could not be undone.')
    } finally {
      setUndoBusy(false)
    }
  }

  const maintenanceGuard = () => {
    if (mode === 'remote-owner') {
      if (!online) throw new Error('Maintenance actions require an internet connection.')
      if (snapshot.pending > 0 || snapshot.phase !== 'synced') throw new Error('Wait for queued entry changes to sync before maintenance.')
      if (structureSync.phase !== 'synced') throw new Error('Wait for structural changes to finish syncing.')
      if (securedBatchBlocker) throw new Error(securedBatchBlocker)
    }
    assertSecuredBatchReady()
  }

  const provideMaintenancePreview = async (request: TimelineMaintenanceRequest): Promise<TimelineMaintenancePreview> => {
    if (maintenanceConflict) throw new Error('Reload authoritative owner data before previewing the preserved maintenance draft again.')
    maintenanceGuard()
    setMaintenanceBusy(true)
    setMaintenanceStatus('Loading exact maintenance impact.')
    try {
      if (mode === 'synthetic-preview') {
        const command = domainCommandFromRequest(document, request, syntheticCategoryCascade.current)
        const domainPreview = previewOwnerMaintenance(document, command, trustedToday, accumulationAnchor)
        const result = componentPreviewFromDomain(request, domainPreview, document)
        syntheticMaintenanceByResult.current.set(result, domainPreview)
        setMaintenanceStatus('Exact maintenance impact loaded.')
        return result
      }
      await refreshRevision()
      maintenanceGuard()
      const authoritative = securedBatchSnapshot()
      const serverPreview = await supabaseOperationWorkflowAdapter.previewOwnerMaintenance(
        authoritative.revision,
        maintenanceInputFromRequest(authoritative.document, request),
      )
      maintenanceGuard()
      const afterPreview = securedBatchSnapshot()
      if (serverPreview.expectedStructureVersion !== afterPreview.revision.structureVersion || serverPreview.expectedScoreRevision !== afterPreview.revision.scoreRevision) {
        throw new Error('Owner revision changed while previewing. Review the maintenance action again.')
      }
      const result = componentPreviewFromRemote(request, serverPreview, localIdBySourceKey(authoritative.document))
      remoteMaintenanceByResult.current.set(result, serverPreview)
      maintenanceMutationByResult.current.set(result, mutationId())
      setMaintenanceStatus('Exact maintenance impact loaded.')
      return result
    } catch (caught) {
      setMaintenanceStatus(caught instanceof Error ? caught.message : 'The maintenance preview failed.')
      throw caught
    } finally {
      setMaintenanceBusy(false)
    }
  }

  const applyMaintenance = async (result: TimelineMaintenancePreview, confirmation: TimelineMaintenanceConfirmation) => {
    maintenanceGuard()
    setMaintenanceBusy(true)
    setMaintenanceStatus('Applying maintenance and loading authoritative data.')
    try {
      if (mode === 'synthetic-preview') {
        const domainPreview = syntheticMaintenanceByResult.current.get(result)
        if (!domainPreview) throw new Error('Review the exact maintenance impact before applying.')
        const next = applyOwnerMaintenance(document, domainPreview, domainPreview.deleteSafeguard ? {
          backupDownloaded: confirmation.backupDownloadInitiated,
          confirmationText: confirmation.typedName ?? '',
        } : undefined)
        if (result.request.action === 'archive' && result.request.target.kind === 'category') {
          syntheticCategoryCascade.current.set(result.request.target.id, domainPreview.affectedOperationIds)
        }
        replaceDocument(next, `${result.request.action} ${result.request.target.kind} ${result.request.target.id} from Timeline`)
        setMaintenanceStatus(`${result.request.target.name} maintenance applied.`)
        return
      }
      const serverPreview = remoteMaintenanceByResult.current.get(result)
      const clientMutationId = maintenanceMutationByResult.current.get(result)
      if (!serverPreview || !clientMutationId) throw new Error('Review the exact server maintenance impact before applying.')
      const current = securedBatchSnapshot()
      if (current.revision.structureVersion !== serverPreview.expectedStructureVersion || current.revision.scoreRevision !== serverPreview.expectedScoreRevision) {
        throw new Error('Owner revision changed after preview. Review the maintenance action again.')
      }
      const recoveryKey = ownerMaintenanceRecoveryKey(current.document.ownerId)
      const storedConfirmation = {
        typedName: confirmation.typedName,
        backupChecksum: confirmation.backupChecksum,
        backupDownloadInitiated: confirmation.backupDownloadInitiated,
      }
      const pendingRecovery: OwnerMaintenanceRecovery = {
        version: 1, ownerId: current.document.ownerId, createdAt: new Date().toISOString(), phase: 'apply-pending',
        preview: serverPreview, clientMutationId, confirmation: storedConfirmation,
      }
      await writeWorkflowRecovery(recoveryKey, encodeOwnerMaintenanceRecovery(pendingRecovery))
      setDurableMaintenanceRecovery(pendingRecovery)
      maintenanceGuard()
      let receipt: OwnerMaintenanceReceipt
      try {
        receipt = await supabaseOperationWorkflowAdapter.applyOwnerMaintenance(serverPreview, clientMutationId, storedConfirmation)
      } catch (caught) {
        if (caught instanceof OwnerWorkflowError && caught.status === 409) {
          await writeWorkflowRecovery(recoveryKey, null)
          setDurableMaintenanceRecovery(null)
          setMaintenanceConflict(caught.message)
        }
        throw caught
      }
      const reloadRecovery: OwnerMaintenanceRecovery = {
        version: 1, ownerId: current.document.ownerId, createdAt: new Date().toISOString(), phase: 'reload-after-apply', receipt,
      }
      await writeWorkflowRecovery(recoveryKey, encodeOwnerMaintenanceRecovery(reloadRecovery))
      setDurableMaintenanceRecovery(reloadRecovery)
      setMaintenanceStatus('Maintenance committed. Reloading authoritative owner data.')
      try {
        maintenanceGuard()
        await reloadAuthoritativeStructure()
      } catch (caught) {
        const message = caught instanceof Error ? caught.message : 'Authoritative owner data could not be reloaded.'
        throw new Error(`The maintenance action committed, but authoritative reload failed. Retry Apply safely. ${message}`)
      }
      await writeWorkflowRecovery(recoveryKey, null)
      setDurableMaintenanceRecovery(null)
      setMaintenanceStatus(`${result.request.target.name} maintenance applied and authoritative data loaded.`)
    } catch (caught) {
      setMaintenanceStatus(caught instanceof Error ? caught.message : 'The maintenance action failed.')
      throw caught
    } finally {
      setMaintenanceBusy(false)
    }
  }

  const retryDurableMaintenanceRecovery = async () => {
    if (!durableMaintenanceRecovery || maintenanceBusy) return
    setMaintenanceBusy(true)
    const recoveryKey = ownerMaintenanceRecoveryKey(durableMaintenanceRecovery.ownerId)
    try {
      let recovery = durableMaintenanceRecovery
      if (recovery.phase === 'apply-pending') {
        setMaintenanceStatus('Retrying the saved maintenance request with its original identity.')
        maintenanceGuard()
        let receipt: OwnerMaintenanceReceipt
        try {
          receipt = await supabaseOperationWorkflowAdapter.applyOwnerMaintenance(recovery.preview, recovery.clientMutationId, recovery.confirmation)
        } catch (caught) {
          if (caught instanceof OwnerWorkflowError && caught.status === 409) {
            await writeWorkflowRecovery(recoveryKey, null)
            setDurableMaintenanceRecovery(null)
            setMaintenanceConflict(caught.message)
          }
          throw caught
        }
        recovery = { version: 1, ownerId: recovery.ownerId, createdAt: new Date().toISOString(), phase: 'reload-after-apply', receipt }
        await writeWorkflowRecovery(recoveryKey, encodeOwnerMaintenanceRecovery(recovery))
        setDurableMaintenanceRecovery(recovery)
      }
      setMaintenanceStatus('Maintenance committed. Reloading authoritative owner data.')
      await reloadAuthoritativeStructure()
      await writeWorkflowRecovery(recoveryKey, null)
      setDurableMaintenanceRecovery(null)
      setMaintenanceStatus('Recovered maintenance data and loaded the authoritative owner document.')
    } catch (caught) {
      setMaintenanceStatus(caught instanceof Error ? caught.message : 'Maintenance recovery remains available for retry.')
    } finally {
      setMaintenanceBusy(false)
    }
  }

  const reloadAfterMaintenanceConflict = async () => {
    if (!maintenanceConflict || maintenanceBusy) return
    setMaintenanceBusy(true)
    try {
      await reloadAuthoritativeStructure()
      setMaintenanceConflict(null)
      setMaintenanceStatus('Authoritative owner data loaded. Review the preserved maintenance draft again.')
    } catch (caught) {
      setMaintenanceStatus(caught instanceof Error ? caught.message : 'Authoritative owner data could not be reloaded.')
    } finally {
      setMaintenanceBusy(false)
    }
  }

  const synchronizeScroll = (event: UIEvent<HTMLDivElement>) => {
    const left = event.currentTarget.scrollLeft
    synchronizedScrollLeft.current = left
    for (const scroller of scrollers.current) if (scroller !== event.currentTarget) scroller.scrollLeft = left
  }

  const moveCellFocus = (event: KeyboardEvent<HTMLButtonElement>) => {
    if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'].includes(event.key)) return
    const target = historyNavigationTarget(event.currentTarget, event.key as HistoryNavigationKey)
    if (target) { event.preventDefault(); target.focus(); target.scrollIntoView({ block: 'nearest', inline: 'nearest' }) }
  }

  return (
    <div className="screen history-screen">
      <ScreenHeading eyebrow="Past, present and future" title="Timeline" description="Edit valid outcomes across time. Future entries save immediately and remain projected until their date arrives.">
        <div className="history-actions"><button ref={selectButton} type="button" className="button" aria-pressed={selectMode} disabled={batchBusy || durableBatchRecovery !== null} onClick={() => selectMode ? closeSelection() : setSelectMode(true)}>{selectMode ? 'Exit Select' : 'Select'}</button><Link className="button" aria-disabled={batchBusy} onClick={(event) => { if (batchBusy) event.preventDefault() }} to={settingsHref('timeline-structure', location.search)}>Configure Timeline</Link><form className="jump-date" onSubmit={jump}><label>Jump to date<input type="date" disabled={batchBusy} value={jumpDate} onChange={(event) => setJumpDate(event.target.value)} /></label><button type="submit" className="button" disabled={batchBusy}>Go</button></form><div className="segmented" aria-label="Timeline range">{ranges.map((item) => <button key={item} type="button" disabled={batchBusy} aria-pressed={!drill && range === item} onClick={() => { setRange(item); setDrill(null); setFocusEnd(null) }}>{item}</button>)}</div></div>
      </ScreenHeading>
      <p className="sr-only" role="status" aria-live="polite">{batchStatus}</p>
      <p className="sr-only" role="status" aria-live="polite">{maintenanceStatus}</p>
      {undoState && <section className="timeline-batch-undo panel" aria-label="Whole batch Undo"><span>{batchStatus || `${undoState.changed} cells changed as one batch.`}</span><button ref={undoButton} type="button" className="button" disabled={undoBusy || durableBatchBusy || batchBusy || reloadRecovery !== null} onClick={() => void requestUndo()}>{undoBusy ? 'Undoing…' : 'Undo whole batch'}</button></section>}
      {reloadRecovery && (!selectMode || selectedOperationIds.length === 0) && <section className="timeline-reload-recovery panel" role="alert"><strong>Authoritative reload required</strong><span>{reloadRecovery.message}</span><button type="button" className="button" disabled={reloadBusy} onClick={() => void retryAuthoritativeReload()}>{reloadBusy ? 'Reloading…' : 'Retry authoritative reload'}</button></section>}
      {durableBatchRecovery && (durableBatchRecovery.phase === 'apply-pending' || durableBatchRecovery.phase === 'undo-pending') && <section className="timeline-reload-recovery panel" role="alert"><strong>Batch recovery required</strong><span>{durableBatchRecovery.phase === 'apply-pending' ? 'The apply response was interrupted. Retry uses the saved batch identity.' : 'The Undo response was interrupted. Retry uses the saved Undo identity.'}</span><button type="button" className="button" disabled={durableBatchBusy} onClick={() => void retrySavedBatchRecovery()}>{durableBatchBusy ? 'Recovering…' : durableBatchRecovery.phase === 'apply-pending' ? 'Retry saved batch' : 'Retry saved Undo'}</button></section>}
      {durableMaintenanceRecovery && <section className="timeline-reload-recovery panel" role="alert"><strong>Maintenance recovery required</strong><span>{durableMaintenanceRecovery.phase === 'apply-pending' ? 'A maintenance response was interrupted. Retry uses the saved request identity without duplicating changes.' : 'Maintenance committed. Load the authoritative owner document before continuing.'}</span><button type="button" className="button" disabled={maintenanceBusy} onClick={() => void retryDurableMaintenanceRecovery()}>{maintenanceBusy ? 'Recovering…' : durableMaintenanceRecovery.phase === 'apply-pending' ? 'Retry saved maintenance' : 'Reload authoritative data'}</button></section>}
      {maintenanceConflict && <section className="timeline-reload-recovery panel" role="alert"><strong>Maintenance draft preserved</strong><span>{maintenanceConflict} Reload authoritative data, then review the preserved draft again.</span><button type="button" className="button" disabled={maintenanceBusy} onClick={() => void reloadAfterMaintenanceConflict()}>{maintenanceBusy ? 'Reloading…' : 'Reload authoritative data'}</button></section>}
      <div className="history-legend panel" aria-label="Timeline legend">
        {['T', 'W', 'C', 'B'].map((code) => <span key={code}><b data-code={code}>{code}</b>{code === 'B' ? 'Bye' : code === 'C' ? 'Adjust' : code === 'W' ? 'Winning' : 'Tick'}</span>)}
        <span><b className="inactive-swatch" />Not scheduled</span>
        <span><b className="projected-swatch" />Projected future</span>
        <button type="button" className="button compact-button bye-provenance-toggle" aria-pressed={showByeSources} onClick={() => setShowByeSources((shown) => !shown)}>{showByeSources ? 'Hide Bye sources' : 'Show Bye sources'}</button>
        {showByeSources && <span className="bye-provenance-explanation" role="status">Rule-generated Byes recalculate from schedules. Stored Bye entries remain until changed or cleared.</span>}
      </div>
      <fieldset className="timeline-archived-filters panel">
        <legend>Archived operation rows</legend>
        <label><input type="checkbox" checked={includePeriodArchived} disabled={showAllArchived || maintenanceBusy || batchBusy} onChange={(event) => setIncludePeriodArchived(event.target.checked)} />Include archived operations active during this period</label>
        <label><input type="checkbox" checked={showAllArchived} disabled={maintenanceBusy || batchBusy} onChange={(event) => { setShowAllArchived(event.target.checked); if (event.target.checked) setIncludePeriodArchived(false) }} />Show all archived operations</label>
      </fieldset>
      <div className="range-note">
        <span>{drill ? `Daily drilldown: ${drill.start} to ${drill.end}` : focusEnd ? `Timeline ending ${focusEnd}` : `${buckets.length} ${buckets[0]?.axis ?? 'daily'} buckets ending today`}</span>
        <span className="timeline-date-navigation"><button type="button" className="button compact-button" disabled={batchBusy} onClick={() => moveTimeline(-1)}>Previous dates</button><button type="button" className="button compact-button" disabled={batchBusy || (!focusEnd && !drill)} onClick={backToToday}>Today</button><button type="button" className="button compact-button" disabled={batchBusy} onClick={() => moveTimeline(1)}>Next dates</button>{drill && <button type="button" className="button compact-button" disabled={batchBusy} onClick={() => setDrill(null)}>Back to {range}</button>}</span>
      </div>
      <section className="timeline-score-strip panel" aria-label="Timeline scores">
        <header><strong>Scores</strong><span>Daily scores by date. Operation averages use the 7 and 28 complete prior days.</span></header>
        <div ref={(node) => { if (node) scrollers.current.add(node) }} className={`matrix-scroll timeline-score-scroll range-${range}`} tabIndex={0} aria-label="Timeline scores, horizontally scrollable" onScroll={synchronizeScroll}>
          <table className="timeline-matrix-table" style={{ minWidth: `calc(var(--timeline-leading-width) + ${buckets.length * (showByeSources ? 82 : 64)}px)` }} aria-label="Overall scores by Timeline date">
            <TimelineMatrixColumns buckets={buckets} />
            <thead><tr><th className="timeline-score-label" scope="col" colSpan={3}><span>Daily score<small>{buckets[0]?.axis === 'daily' ? 'Overall' : 'Bucket average'}</small></span></th>{buckets.map((bucket) => {
              const score = timelineBucketScore(document, bucket, trustedToday)
              const dateState = timelineDateState(bucket, trustedToday)
              return <th scope="col" className={`timeline-date-${dateState}`} aria-label={`${bucket.label}: ${score === null ? 'no daily score' : `${Math.round(score)} points`}${dateState === 'future' ? ', projected' : ''}`} key={bucket.id}><span>{bucket.label}<strong>{formatTimelineScore(score)}</strong>{dateState === 'future' && <small>Projected</small>}</span></th>
            })}</tr></thead>
          </table>
        </div>
      </section>
      <div className="history-groups">
        {document.categories.map((category) => {
          const visibleSpan = timelineBucketSpan(buckets, trustedToday)
          const visibleOperations = timelineVisibleOperations(document, category.id, trustedToday, visibleSpan.from, visibleSpan.to, { includePeriodArchived, showAllArchived })
          const operations = timelineSortOperations(visibleOperations.map(({ operation }) => operation), operationTrailsById, operationSort)
          const categoryActive = isActiveOn(category.activePeriods, trustedToday)
          if (operations.length === 0 && !(categoryActive && category.operationOrder.length === 0)) return null
          const categoryScores = buckets.flatMap((bucket) => datesInclusive(bucket.start, bucket.end).map((date) => scoreDay(document, date, { categoryId: category.id, asOf: trustedToday }).score)).filter((score): score is number => score !== null)
          const average = categoryScores.length ? Math.round(categoryScores.reduce((sum, score) => sum + score, 0) / categoryScores.length) : null
          const includesProjection = buckets.some((bucket) => bucket.end > trustedToday)
          const rovingAssigned = { current: false }
          const categoryTarget: TimelineMaintenanceTarget = {
            kind: 'category', id: category.id, name: category.name, active: categoryActive,
            operations: category.operationOrder.flatMap((id) => {
              const operation = document.operations.find((candidate) => candidate.id === id)
              return operation ? [{ id, name: operation.name, active: isActiveOn(operation.activePeriods, trustedToday) }] : []
            }),
          }
          return <section className={`history-card panel${categoryActive ? '' : ' archived-category'}`} key={category.id}>
            <header><div><strong>{category.name}</strong><small>{categoryActive ? includesProjection ? 'Projected visible-period average' : 'Visible-period average' : 'Archived category'}</small></div><span className="history-card-actions">{selectMode && <button type="button" className="button compact-button" disabled={batchBusy} aria-label={`Select ${category.name} category across visible dates`} onClick={() => selectOperations(operations.map((operation) => operation.id))}>Select category</button>}<TimelineMaintenanceControls target={categoryTarget} today={trustedToday} categories={document.categories.filter((candidate) => isActiveOn(candidate.activePeriods, trustedToday)).map((candidate) => ({ id: candidate.id, name: candidate.name }))} disabled={batchBusy || selectMode || maintenanceBusy || maintenanceConflict !== null || reloadRecovery !== null || durableMaintenanceRecovery !== null || mode === 'remote-owner' && securedBatchBlocker !== null} onPreview={provideMaintenancePreview} onApply={applyMaintenance} onDownloadBackup={downloadTimelineMaintenanceBackup} /></span><strong>{average ?? '—'}{includesProjection && <small>Projected</small>}</strong></header>
            <div ref={(node) => { if (node) scrollers.current.add(node) }} className={`matrix-scroll range-${range}`} tabIndex={0} aria-label={`${category.name} Timeline, horizontally scrollable`} onScroll={synchronizeScroll}>
              <table className="timeline-matrix-table" style={{ minWidth: `calc(var(--timeline-leading-width) + ${buckets.length * (showByeSources ? 82 : 64)}px)` }}>
                <TimelineMatrixColumns buckets={buckets} />
                <thead><tr>
                  <th scope="col" aria-sort={operationSort?.column === 'operation' ? operationSort.direction : 'none'}><button type="button" className="timeline-sort-button" onClick={() => changeOperationSort('operation')}>Operation</button></th>
                  <th className="timeline-trail-heading timeline-trail-7d" scope="col" aria-sort={operationSort?.column === '7d' ? operationSort.direction : 'none'}><button type="button" className="timeline-sort-button" aria-label="Sort operations by 7 day trailing average" onClick={() => changeOperationSort('7d')}>7d</button></th>
                  <th className="timeline-trail-heading timeline-trail-28d" scope="col" aria-sort={operationSort?.column === '28d' ? operationSort.direction : 'none'}><button type="button" className="timeline-sort-button" aria-label="Sort operations by 28 day trailing average" onClick={() => changeOperationSort('28d')}>28d</button></th>
                  {buckets.map((bucket) => { const dateState = timelineDateState(bucket, trustedToday); return <th scope="col" className={`timeline-date-${dateState}`} key={bucket.id}>{selectMode ? <button type="button" className="bucket-drill" disabled={batchBusy} aria-label={`Select ${bucket.label} in ${category.name}, ${bucket.start} to ${bucket.end}`} onClick={() => selectOperations(operations.map((operation) => operation.id), [bucket])}>{bucket.label}{dateState === 'future' && <small>Projected</small>}</button> : bucket.axis === 'daily' ? <span>{bucket.label}{dateState === 'future' && <small>Projected</small>}</span> : <button type="button" className="bucket-drill" onClick={() => setDrill(bucket)} aria-label={`Drill into ${bucket.label}${dateState === 'future' ? ', projected' : ''}`}>{bucket.label}{dateState === 'future' && <small>Projected</small>}</button>}</th> })}
                </tr></thead>
                <tbody>{operations.map((operation) => {
                  const operationActive = isActiveOn(operation.activePeriods, trustedToday)
                  const operationTarget: TimelineMaintenanceTarget = { kind: 'operation', id: operation.id, name: operation.name, active: operationActive, categoryId: operation.categoryId }
                  const [trail7, trail28] = operationTrailsById.get(operation.id) ?? []
                  return <tr className={operationActive ? undefined : 'archived-operation-row'} key={operation.id}>
                  <th scope="row"><div className="timeline-operation-heading"><span>{operation.name}{operation.private && <span className="privacy-chip">Private</span>}</span><TimelineMaintenanceControls target={operationTarget} today={trustedToday} categories={document.categories.filter((candidate) => isActiveOn(candidate.activePeriods, trustedToday)).map((candidate) => ({ id: candidate.id, name: candidate.name }))} disabled={batchBusy || selectMode || maintenanceBusy || maintenanceConflict !== null || reloadRecovery !== null || durableMaintenanceRecovery !== null || mode === 'remote-owner' && securedBatchBlocker !== null} onPreview={provideMaintenancePreview} onApply={applyMaintenance} onDownloadBackup={downloadTimelineMaintenanceBackup} /></div><small>{operationActive ? currentRule(operation, trustedToday)?.period.kind ?? 'Inactive' : 'Archived, inspect or restore'}</small>{selectMode && <button type="button" className="timeline-row-select" disabled={batchBusy} aria-label={`Select ${operation.name} row in ${category.name} across visible dates`} onClick={() => selectOperations([operation.id])}>Select row</button>}</th>
                  <td className="operation-trail-cell timeline-trail-7d" aria-label={`${operation.name}, 7 day trailing average: ${trail7?.score === null || trail7 === undefined ? 'unavailable' : `${Math.round(trail7.score)} points`}`}>{formatTimelineScore(trail7?.score ?? null)}</td>
                  <td className="operation-trail-cell timeline-trail-28d" aria-label={`${operation.name}, 28 day trailing average: ${trail28?.score === null || trail28 === undefined ? 'unavailable' : `${Math.round(trail28.score)} points`}`}>{formatTimelineScore(trail28?.score ?? null)}</td>
                  {buckets.map((bucket) => {
                    if (bucket.axis !== 'daily') {
                      const score = timelineBucketScore(document, bucket, trustedToday, [operation.id])
                      const projected = bucket.end > trustedToday
                      const selected = selectMode && selectedOperationIds.includes(operation.id) && selectionFrom <= bucket.start && selectionTo >= bucket.end
                      const previewed = previewResult?.preview.cells.some((cell) => cell.operationId === operation.id && cell.date >= bucket.start && cell.date <= bucket.end) === true
                      return <td className={`timeline-date-${timelineDateState(bucket, trustedToday)}`} key={`${operation.id}-${bucket.id}`}><button type="button" className={`history-cell aggregate-cell${projected ? ' projected' : ''}${selected ? ' selected' : ''}${previewed ? ' preview-change' : ''}`} disabled={batchBusy} aria-pressed={selectMode ? selected : undefined} tabIndex={rovingHistoryTabIndex(batchBusy, rovingAssigned)} onKeyDown={moveCellFocus} onClick={() => selectMode ? selectOperations([operation.id], [bucket]) : setDrill(bucket)} aria-label={`${operation.name}, ${bucket.label}: ${score === null ? 'no score' : `${Math.round(score)} points`}${projected ? ', projected' : ''}${selected ? ', selected' : ''}${previewed ? ', will change' : ''}`}><span>{score === null ? '—' : Math.round(score)}</span>{projected && <small>Projected</small>}</button></td>
                    }
                    const contribution = scoreDay(document, bucket.start, { operationIds: [operation.id], asOf: trustedToday }).contributions[0]
                    const entry = entryFor(document, operation.id, bucket.start)
                    const { inactive, recordedOutsideSchedule, disabled } = historyCellState(contribution, entry)
                    if (!contribution) return <td key={`${operation.id}-${bucket.id}`} />
                    const presentation = timelineCellPresentation(contribution)
                    const byeSource = presentation.code === 'B' ? contribution.displaySource === 'generated-schedule' ? 'Rule-generated' : 'Stored entry' : null
                    const dateState = timelineDateState(bucket, trustedToday)
                    const key = `${operation.id}\u0000${bucket.start}`
                    const selected = selectMode && selectedCells.has(key)
                    const previewed = previewCells.has(key)
                    return <td className={`timeline-date-${dateState}`} key={`${operation.id}-${bucket.id}`}><button type="button" className={`${recordedOutsideSchedule ? 'history-cell outside-schedule' : inactive ? 'history-cell inactive' : 'history-cell'}${contribution.projected ? ' projected' : ''}${selected ? ' selected' : ''}${previewed ? ' preview-change' : ''}${showByeSources && byeSource ? ' bye-source-visible' : ''}`} data-code={presentation.code ?? ''} data-display-source={contribution.displaySource} data-operation-id={operation.id} data-date={bucket.start} disabled={batchBusy || (!selectMode && disabled)} aria-pressed={selectMode ? selected : undefined} tabIndex={rovingHistoryTabIndex(batchBusy || (!selectMode && disabled), rovingAssigned)} onKeyDown={moveCellFocus} aria-label={`${operation.name}, ${bucket.label}: ${presentation.description}${selected ? ', selected' : ''}${previewed ? ', will change' : ''}`} onPointerDown={(event) => startDrag(event, operation.id, bucket.start)} onPointerEnter={() => extendDrag(operation.id, bucket.start)} onPointerMove={() => extendDrag(operation.id, bucket.start)} onClick={(event: MouseEvent<HTMLButtonElement>) => selectMode ? selectCell(operation.id, bucket.start, event.shiftKey) : cycle(operation.id, bucket.start)}><span>{presentation.text}</span>{showByeSources && byeSource && <small className="bye-source-label">{byeSource}</small>}</button></td>
                  })}
                </tr>})}</tbody>
              </table>
            </div>
          </section>
        })}
      </div>
      {selectMode && selectedOperationIds.length > 0 && !dragActive && <aside className="timeline-batch-sheet panel" aria-label="Timeline batch controls">
        {reloadRecovery && <section className="timeline-reload-recovery" role="alert"><strong>Authoritative reload required</strong><span>{reloadRecovery.message}</span><button type="button" className="button" disabled={reloadBusy || batchBusy} onClick={() => void retryAuthoritativeReload()}>{reloadBusy ? 'Reloading…' : 'Retry authoritative reload'}</button></section>}
        <div className="segmented timeline-batch-variant" aria-label="Selected-cell action">
          <button type="button" disabled={batchBusy} aria-pressed={batchVariant === 'general'} onClick={() => { clearPreview(); setBatchVariant('general') }}>Batch edit</button>
          <button type="button" disabled={batchBusy} aria-pressed={batchVariant === 'bye-period'} onClick={() => { clearPreview(); setBatchVariant('bye-period') }}>Add Bye period</button>
        </div>
        <TimelineBatchControls
          variant={batchVariant}
          document={document}
          trustedToday={trustedToday}
          selectedOperationIds={selectedOperationIds}
          initialFrom={selectionFrom}
          initialTo={selectionTo}
          previewProvider={provideBatchPreview}
          onPreview={(result) => {
            setPreviewResult(result)
            remotePreview.current = remotePreviewByResult.current.get(result) ?? null
            clientBatchId.current = remotePreview.current ? mutationId() : null
            setBatchStatus(`${result.counts.changed} cells will change, ${result.counts.protected} protected, ${result.counts.skipped} skipped.`)
          }}
          onPreviewCleared={clearPreview}
          onBusyChange={setBatchBusy}
          onApply={applyBatch}
          onCancel={closeSelection}
        />
      </aside>}
    </div>
  )
}
